import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { can, DISABLED_BTN_CLS, permissionTitle } from "../lib/permissions";
import {
    LifecycleBadge,
    RiskBadge,
    KillSwitchBadge,
    ReadinessBar,
    PRODUCT_TYPES,
    LIFECYCLE_STATUSES,
    RISK_TIERS,
} from "../components/Badges";
import {
    Plus,
    Edit2,
    Trash2,
    Power,
    AlertTriangle,
    X,
    Loader2,
    ExternalLink,
    Filter,
    ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
    name: "",
    product_type: "In-House",
    control_mode_id: "",
    lifecycle_status: "Under Review",
    readiness_score: 0,
    kill_switch_active: false,
    risk_tier: "Low",
};

function ControlModeSummary({ mode }) {
    if (!mode) {
        return (
            <div className="border border-signal/40 bg-signal/5 px-3 py-2 text-[11px] font-mono text-signal mt-2" data-testid="mode-summary-required">
                Select a control mode — every product must follow a defined gate sequence.
            </div>
        );
    }
    const gateCount = (mode.gate_sequence || []).length;
    const requiresExec = (mode.gate_sequence || []).includes(10) || mode.mode_name === "Critical Systems Track";
    // Rough heuristic for ETA based on track rigor (same number used as a tactical guide).
    const eta = mode.mode_name === "Fast Track" ? "2-4 weeks" : mode.mode_name === "Critical Systems Track" ? "12-20 weeks" : "6-10 weeks";
    return (
        <div className="border border-hairline bg-jet p-3 mt-2 space-y-1.5" data-testid="mode-summary-panel">
            <div className="flex items-center justify-between">
                <div className="overline">{mode.mode_name}</div>
                <span className="text-[10px] font-mono text-signal tabular-nums">
                    {gateCount} gate{gateCount === 1 ? "" : "s"}
                </span>
            </div>
            <div className="text-[11px] text-snow-dim font-mono leading-relaxed">{mode.description}</div>
            <div className="text-[10px] text-snow-muted font-mono uppercase tracking-wider pt-1.5 border-t border-hairline">
                <span className="text-snow-dim">{mode.mode_name}</span> requires {gateCount} gate{gateCount === 1 ? "" : "s"}
                {requiresExec ? " + Executive Sign-off" : ""}. ETA to Mission Ready: <span className="text-snow">{eta}</span>.
            </div>
        </div>
    );
}

export default function Products() {
    const { user } = useAuth();
    const canManage = can(user, "manageProducts");
    const canDelete = can(user, "deleteProduct");
    const canKill = can(user, "killSwitch");
    const [products, setProducts] = useState([]);
    const [modes, setModes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null = closed, {} = create, {...} = edit
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [killTarget, setKillTarget] = useState(null);
    const [killReason, setKillReason] = useState("");
    const [killSubmitting, setKillSubmitting] = useState(false);
    const [killError, setKillError] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [error, setError] = useState("");

    // Build Prompt #16 — Products page filter + sort header (default: Mission Ready).
    const [lifecycleFilter, setLifecycleFilter] = useState("Mission Ready");
    const [riskFilter, setRiskFilter] = useState("All");
    const [typeFilter, setTypeFilter] = useState("All");
    const [sortBy, setSortBy] = useState("readiness_desc"); // readiness_desc | readiness_asc | name | risk
    const [searchTerm, setSearchTerm] = useState("");

    const refresh = async () => {
        setLoading(true);
        try {
            const [p, m] = await Promise.all([
                api.get("/products").then((r) => r.data),
                api.get("/control-modes").then((r) => r.data),
            ]);
            setProducts(p);
            setModes(m);
        } catch (err) {
            const status = err?.response?.status;
            // 401 is handled globally by the api interceptor (revalidate / redirect).
            // Surface anything else to the user, but never let it become an unhandled rejection.
            if (status !== 401) {
                console.warn("[Products] refresh failed:", status, err?.message);
                setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Failed to load products");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    // Build Prompt #16 — derived filter + sort.
    const visibleProducts = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        let list = products.filter((p) => {
            if (lifecycleFilter !== "All" && p.lifecycle_status !== lifecycleFilter) return false;
            if (riskFilter !== "All" && p.risk_tier !== riskFilter) return false;
            if (typeFilter !== "All" && p.product_type !== typeFilter) return false;
            if (term && !p.name.toLowerCase().includes(term)) return false;
            return true;
        });
        const lifecycleRank = { "Mission Ready": 0, "Under Review": 1, "Restricted": 2, "Suspended": 3, "Retired": 4 };
        const riskRank = { Critical: 0, High: 1, Medium: 2, Low: 3 };
        switch (sortBy) {
            case "readiness_asc":
                list = list.slice().sort((a, b) => (a.readiness_score ?? 0) - (b.readiness_score ?? 0));
                break;
            case "name":
                list = list.slice().sort((a, b) => a.name.localeCompare(b.name));
                break;
            case "risk":
                list = list.slice().sort((a, b) => (riskRank[a.risk_tier] ?? 9) - (riskRank[b.risk_tier] ?? 9));
                break;
            case "lifecycle":
                list = list.slice().sort((a, b) => (lifecycleRank[a.lifecycle_status] ?? 9) - (lifecycleRank[b.lifecycle_status] ?? 9));
                break;
            case "readiness_desc":
            default:
                list = list.slice().sort((a, b) => (b.readiness_score ?? 0) - (a.readiness_score ?? 0));
        }
        return list;
    }, [products, lifecycleFilter, riskFilter, typeFilter, sortBy, searchTerm]);

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditing({});
        setError("");
    };
    const openEdit = (p) => {
        setForm({
            name: p.name,
            product_type: p.product_type,
            control_mode_id: p.control_mode_id || "",
            lifecycle_status: p.lifecycle_status,
            readiness_score: p.readiness_score,
            kill_switch_active: p.kill_switch_active,
            risk_tier: p.risk_tier,
        });
        setEditing(p);
        setError("");
    };
    const close = () => setEditing(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.control_mode_id) {
            setError("Control mode is required. Select a track to define this product's gate sequence.");
            return;
        }
        setSaving(true);
        setError("");
        const payload = {
            ...form,
            readiness_score: Number(form.readiness_score),
        };
        // kill_switch_active is never sent via product create/update — it has its own reason-gated endpoint
        delete payload.kill_switch_active;
        try {
            if (editing && editing.id) {
                await api.patch(`/products/${editing.id}`, payload);
                toast.success("Product updated");
            } else {
                await api.post("/products", { ...payload, kill_switch_active: false });
                toast.success("Product created");
            }
            close();
            refresh();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const confirmKill = async () => {
        if (!killTarget) return;
        const trimmed = killReason.trim();
        if (trimmed.length < 3) {
            setKillError("Reason is required (min 3 characters).");
            return;
        }
        setKillSubmitting(true);
        setKillError("");
        const next = !killTarget.kill_switch_active;
        try {
            await api.post(`/products/${killTarget.id}/kill-switch`, { active: next, reason: trimmed });
            toast.success(next ? "Kill switch ARMED" : "Kill switch released");
            setKillTarget(null);
            setKillReason("");
            refresh();
        } catch (err) {
            setKillError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setKillSubmitting(false);
        }
    };

    const closeKill = () => {
        setKillTarget(null);
        setKillReason("");
        setKillError("");
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/products/${deleteTarget.id}`);
            toast.success("Product deleted");
            setDeleteTarget(null);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const modeName = (id) => modes.find((m) => m.id === id)?.mode_name || "—";

    return (
        <div className="space-y-6" data-testid="products-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="overline">Master Records / Products</div>
                    <h1 className="heading-display text-4xl mt-1">Products</h1>
                    <p className="text-snow-dim text-sm mt-2 max-w-2xl">
                        Every system in scope. Manage lifecycle, readiness, risk tier, and the kill switch.
                    </p>
                </div>
                <ProductsHeaderCTA canCreate={can(user, "createProduct")} user={user} openCreate={openCreate} />
            </div>

            {/* Build Prompt #16 — Products Filter + Sort header */}
            <div className="border border-[#2A2A2A] bg-jet-elev1 px-5 py-4" data-testid="products-filter-header">
                <div className="flex items-center gap-2 mb-3">
                    <Filter className="w-3 h-3 text-signal" />
                    <span className="overline">Filter & Sort</span>
                    <span className="ml-auto text-[11px] font-mono text-snow-muted tabular-nums" data-testid="products-result-count">
                        {visibleProducts.length} of {products.length}
                    </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <ProductFilterSelect
                        label="Lifecycle"
                        value={lifecycleFilter}
                        onChange={setLifecycleFilter}
                        options={["All", ...LIFECYCLE_STATUSES]}
                        testid="filter-lifecycle"
                    />
                    <ProductFilterSelect
                        label="Risk Tier"
                        value={riskFilter}
                        onChange={setRiskFilter}
                        options={["All", ...RISK_TIERS]}
                        testid="filter-risk"
                    />
                    <ProductFilterSelect
                        label="Type"
                        value={typeFilter}
                        onChange={setTypeFilter}
                        options={["All", ...PRODUCT_TYPES]}
                        testid="filter-type"
                    />
                    <div>
                        <label className="overline block mb-1.5 flex items-center gap-1"><ArrowUpDown className="w-2.5 h-2.5" /> Sort</label>
                        <select
                            data-testid="filter-sort"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="cerb-input"
                        >
                            <option value="readiness_desc">Readiness (high → low)</option>
                            <option value="readiness_asc">Readiness (low → high)</option>
                            <option value="name">Name (A → Z)</option>
                            <option value="risk">Risk Tier (high → low)</option>
                            <option value="lifecycle">Lifecycle (Mission Ready first)</option>
                        </select>
                    </div>
                    <div>
                        <label className="overline block mb-1.5">Search</label>
                        <input
                            data-testid="filter-search"
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Product name…"
                            className="cerb-input"
                        />
                    </div>
                </div>
            </div>

            <div className="border border-hairline bg-jet-elev1">
                {error && !loading && (
                    <div className="border-b border-red-500/40 bg-red-500/5 px-5 py-3 text-xs font-mono text-red-400" data-testid="products-error-banner">
                        {error}
                    </div>
                )}
                {loading ? (
                    <div className="p-8 text-snow-dim font-mono text-sm">Loading…</div>
                ) : visibleProducts.length === 0 ? (
                    <div className="p-12 text-center" data-testid="products-empty-state">
                        <div className="overline">{products.length === 0 ? "No Products" : "No Matches"}</div>
                        <div className="heading-display text-xl mt-2">
                            {products.length === 0 ? "Roster empty" : "Adjust your filters"}
                        </div>
                        {products.length > 0 && (
                            <button
                                onClick={() => { setLifecycleFilter("All"); setRiskFilter("All"); setTypeFilter("All"); setSearchTerm(""); }}
                                data-testid="products-clear-filters-btn"
                                className="mt-4 px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border border-signal text-signal hover:bg-signal hover:text-jet transition-colors"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                ) : (
                    <table className="w-full" data-testid="products-table">
                        <thead>
                            <tr className="border-b border-hairline">
                                <Th>Product</Th>
                                <Th>Type</Th>
                                <Th>Control Mode</Th>
                                <Th>Lifecycle</Th>
                                <Th>Risk</Th>
                                <Th className="w-[180px]">Readiness</Th>
                                <Th>Kill Switch</Th>
                                <Th>Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleProducts.map((p) => (
                                <tr
                                    key={p.id}
                                    data-testid={`product-row-${p.id}`}
                                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                                >
                                    <Td><div className="font-mono text-sm">{p.name}</div></Td>
                                    <Td><span className="overline">{p.product_type}</span></Td>
                                    <Td><span className="text-xs font-mono text-snow-dim">{modeName(p.control_mode_id)}</span></Td>
                                    <Td><LifecycleBadge status={p.lifecycle_status} /></Td>
                                    <Td><RiskBadge tier={p.risk_tier} /></Td>
                                    <Td><ReadinessBar value={p.readiness_score} /></Td>
                                    <Td><KillSwitchBadge active={p.kill_switch_active} /></Td>
                                    <Td>
                                        <div className="flex items-center gap-2">
                                            <Link
                                                to={`/products/${p.id}`}
                                                data-testid={`view-product-${p.id}`}
                                                title="Open command center"
                                                className="p-1.5 border border-hairline text-snow-dim hover:border-signal hover:text-signal transition-colors inline-flex"
                                            >
                                                <ExternalLink className="w-3.5 h-3.5" />
                                            </Link>
                                            <button
                                                data-testid={`kill-toggle-${p.id}`}
                                                title={canKill ? (p.kill_switch_active ? "Release kill switch" : "Engage kill switch") : permissionTitle(user, "killSwitch")}
                                                onClick={() => canKill && setKillTarget(p)}
                                                disabled={!canKill}
                                                className={`p-1.5 border transition-colors ${
                                                    !canKill
                                                        ? DISABLED_BTN_CLS
                                                        : p.kill_switch_active
                                                        ? "border-signal text-signal hover:bg-signal hover:text-jet"
                                                        : "border-hairline text-snow-dim hover:border-signal hover:text-signal"
                                                }`}
                                            >
                                                <Power className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                data-testid={`edit-product-${p.id}`}
                                                onClick={() => canManage && openEdit(p)}
                                                disabled={!canManage}
                                                title={permissionTitle(user, "manageProducts")}
                                                className={`p-1.5 border transition-colors ${!canManage ? DISABLED_BTN_CLS : "border-hairline text-snow-dim hover:border-signal hover:text-signal"}`}
                                            >
                                                <Edit2 className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                data-testid={`delete-product-${p.id}`}
                                                onClick={() => canDelete && setDeleteTarget(p)}
                                                disabled={!canDelete}
                                                title={permissionTitle(user, "deleteProduct")}
                                                className={`p-1.5 border transition-colors ${!canDelete ? DISABLED_BTN_CLS : "border-hairline text-snow-dim hover:border-red-500 hover:text-red-400"}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Edit / Create Modal */}
            {editing !== null && (
                <Modal title={editing.id ? "Edit Product" : "New Product"} onClose={close}>
                    <form onSubmit={submit} className="space-y-4" data-testid="product-form">
                        <Field label="Name">
                            <input
                                data-testid="product-name-input"
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="cerb-input"
                            />
                        </Field>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Product Type">
                                <select
                                    data-testid="product-type-select"
                                    value={form.product_type}
                                    onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                                    className="cerb-input"
                                >
                                    {PRODUCT_TYPES.map((t) => <option key={t}>{t}</option>)}
                                </select>
                            </Field>
                            <Field label="Risk Tier">
                                <select
                                    data-testid="product-risk-select"
                                    value={form.risk_tier}
                                    onChange={(e) => setForm({ ...form, risk_tier: e.target.value })}
                                    className="cerb-input"
                                >
                                    {RISK_TIERS.map((t) => <option key={t}>{t}</option>)}
                                </select>
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label={<>Control Mode <span className="text-signal">*</span></>}>
                                <select
                                    data-testid="product-mode-select"
                                    required
                                    value={form.control_mode_id}
                                    onChange={(e) => setForm({ ...form, control_mode_id: e.target.value })}
                                    className="cerb-input"
                                >
                                    <option value="" disabled>— Select a track —</option>
                                    {modes.map((m) => (
                                        <option key={m.id} value={m.id}>
                                            {m.mode_name} ({(m.gate_sequence || []).length} gates)
                                        </option>
                                    ))}
                                </select>
                                <ControlModeSummary mode={modes.find((m) => m.id === form.control_mode_id)} />
                            </Field>
                            <Field label="Lifecycle Status">
                                <select
                                    data-testid="product-lifecycle-select"
                                    value={form.lifecycle_status}
                                    onChange={(e) => setForm({ ...form, lifecycle_status: e.target.value })}
                                    className="cerb-input"
                                >
                                    {LIFECYCLE_STATUSES.map((s) => <option key={s}>{s}</option>)}
                                </select>
                            </Field>
                        </div>
                        <Field label={`Readiness Score: ${form.readiness_score}`}>
                            <input
                                data-testid="product-readiness-input"
                                type="range"
                                min="0"
                                max="100"
                                value={form.readiness_score}
                                onChange={(e) => setForm({ ...form, readiness_score: Number(e.target.value) })}
                                className="w-full accent-signal"
                            />
                        </Field>
                        <Field label="Kill Switch">
                            <div className="flex items-center justify-between border border-hairline px-3 py-2">
                                <div>
                                    <div className="text-xs font-mono">
                                        {editing && editing.id && editing.kill_switch_active
                                            ? <span className="text-signal">CURRENTLY ARMED</span>
                                            : <span className="text-snow-dim">Currently inactive</span>}
                                    </div>
                                    <div className="text-[10px] text-snow-muted mt-0.5 font-mono uppercase tracking-wider">
                                        Toggle requires reason — use Power button on the row.
                                    </div>
                                </div>
                            </div>
                        </Field>

                        {error && <div className="text-red-400 text-xs font-mono">{error}</div>}

                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button type="button" onClick={close} className="cerb-btn-secondary" data-testid="product-cancel-btn">Cancel</button>
                            <button type="submit" disabled={saving} className="cerb-btn-primary" data-testid="product-save-btn">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Kill switch confirm */}
            {killTarget && (
                <Modal title="Kill Switch Confirmation" onClose={closeKill} accent>
                    <div className="space-y-4" data-testid="kill-switch-confirm-modal">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-6 h-6 text-signal flex-shrink-0 mt-1" />
                            <div className="flex-1">
                                <div className="heading-display text-lg">
                                    {killTarget.kill_switch_active ? "Release kill switch?" : "Engage kill switch?"}
                                </div>
                                <div className="text-sm text-snow-dim mt-2 font-mono leading-relaxed">
                                    Target: <span className="text-snow">{killTarget.name}</span>
                                </div>
                                <div className="text-xs text-snow-muted mt-2">
                                    {killTarget.kill_switch_active
                                        ? "Releasing will remove emergency lockout and restore operational mode."
                                        : "Engaging will trigger an immediate emergency lockout. This action is logged in the audit trail."}
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="overline block mb-2">
                                Reason <span className="text-signal">*</span>
                            </label>
                            <textarea
                                data-testid="kill-reason-input"
                                rows={3}
                                autoFocus
                                value={killReason}
                                onChange={(e) => setKillReason(e.target.value)}
                                placeholder={killTarget.kill_switch_active
                                    ? "e.g. Vulnerability patched, validated by Security."
                                    : "e.g. Critical security finding — emergency lockout."}
                                className="cerb-input"
                                maxLength={500}
                            />
                            <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider flex justify-between">
                                <span>Required • will be written to audit_logs</span>
                                <span className="tabular-nums">{killReason.length}/500</span>
                            </div>
                        </div>

                        {killError && (
                            <div className="border border-red-500/40 bg-red-500/5 text-red-400 px-3 py-2 text-xs font-mono" data-testid="kill-reason-error">
                                {killError}
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={closeKill} className="cerb-btn-secondary" data-testid="kill-cancel-btn">Cancel</button>
                            <button
                                onClick={confirmKill}
                                disabled={killSubmitting || killReason.trim().length < 3}
                                className="cerb-btn-primary"
                                data-testid="kill-confirm-btn"
                            >
                                {killSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (killTarget.kill_switch_active ? "Release" : "Engage")}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Delete confirm */}
            {deleteTarget && (
                <Modal title="Delete Product" onClose={() => setDeleteTarget(null)}>
                    <div className="space-y-4" data-testid="delete-confirm-modal">
                        <div className="text-sm">
                            Permanently delete <span className="text-signal font-bold">{deleteTarget.name}</span>?
                        </div>
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={() => setDeleteTarget(null)} className="cerb-btn-secondary" data-testid="delete-cancel-btn">Cancel</button>
                            <button onClick={confirmDelete} className="bg-red-500 text-snow hover:bg-red-600 px-6 py-2 font-bold uppercase tracking-[0.2em] text-xs" data-testid="delete-confirm-btn">
                                Delete
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function Modal({ title, children, onClose, accent }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-jet/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div
                className={`relative w-full max-w-xl bg-jet-elev1 border ${accent ? "border-signal" : "border-hairline"} max-h-[90vh] overflow-y-auto`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={`flex items-center justify-between border-b ${accent ? "border-signal" : "border-hairline"} px-5 py-3`}>
                    <div>
                        <div className="overline">Action</div>
                        <div className="heading-display text-lg">{title}</div>
                    </div>
                    <button onClick={onClose} className="text-snow-dim hover:text-signal" data-testid="modal-close-btn">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

function ProductFilterSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="overline block mb-1.5">{label}</label>
            <select
                data-testid={testid}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="cerb-input"
            >
                {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="overline block mb-2">{label}</label>
            {children}
        </div>
    );
}

function Th({ children, className = "" }) {
    return <th className={`text-left px-5 py-3 overline font-normal ${className}`}>{children}</th>;
}
function Td({ children }) {
    return <td className="px-5 py-3 align-middle">{children}</td>;
}


// Spec: Limit "Add Product" button to admin/executive only (createProduct permission).
// Operators see "Submit Idea" instead — points them at the Concept Intake funnel.
function ProductsHeaderCTA({ canCreate, user, openCreate }) {
    const role = (user?.role || "").toLowerCase();
    const canSubmitIdea = role === "admin" || role === "executive" || role === "operator";

    if (canCreate) {
        return (
            <button
                data-testid="create-product-btn"
                onClick={openCreate}
                title="Add a new product to the registry"
                className="bg-signal text-jet hover:bg-signal-hover px-5 py-2 transition-colors font-bold uppercase tracking-[0.2em] text-xs flex items-center gap-2"
            >
                <Plus className="w-3 h-3" /> New Product
            </button>
        );
    }

    if (canSubmitIdea) {
        return (
            <Link
                to="/concept-intake"
                data-testid="products-submit-idea-cta"
                title="Operators submit ideas through the Concept Intake funnel; leadership promotes them to products."
                className="border border-signal text-signal hover:bg-signal hover:text-jet px-5 py-2 transition-colors font-bold uppercase tracking-[0.2em] text-xs flex items-center gap-2"
            >
                <Plus className="w-3 h-3" /> Submit Idea
            </Link>
        );
    }

    return (
        <span
            data-testid="products-readonly-badge"
            className="border border-[#2A2A2A] text-snow-muted px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em]"
        >
            Read-Only
        </span>
    );
}
